import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Sparkle, GraduationCap, CheckCircle, ArrowRight, Users, CalendarBlank } from "@phosphor-icons/react";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP", "AED", "CHF"];
const DONE_KEY = "tutorpro:onboardingDone";

/**
 * Shown once when a new user has no students yet. Three quick steps:
 * 1) Currency confirmation
 * 2) Add first student CTA (navigates to /ogrenciler)
 * 3) Weekly schedule tip + finish
 */
export default function OnboardingWizard() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [currency, setCurrency] = useState(user?.currency || "TRY");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    // Admins don't need the onboarding wizard
    if (user.role === "admin") return;
    const done = (() => { try { return localStorage.getItem(DONE_KEY) === "1"; } catch { return false; } })();
    if (done) return;
    // Only show when teacher has 0 students
    let ignore = false;
    api.get("/students").then((r) => {
      if (!ignore && r.data.length === 0) setOpen(true);
    }).catch(() => {});
    return () => { ignore = true; };
  }, [user]);

  function dismiss() {
    try { localStorage.setItem(DONE_KEY, "1"); } catch {}
    setOpen(false);
  }

  async function saveCurrency() {
    setSaving(true);
    try {
      await api.patch("/auth/me", { currency });
      await refreshUser();
      setStep(2);
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="max-w-lg" data-testid="onboarding-wizard">
        {/* Header */}
        <div className="flex items-center gap-2 text-[#3A5A40]">
          <GraduationCap size={22} weight="duotone" />
          <span className="font-display font-bold">EduFin</span>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-2 mt-1">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-[#3A5A40]" : "bg-muted"}`}
            />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-5 mt-3" data-testid="onboarding-step-1">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                <Sparkle size={20} weight="duotone" className="text-[#2E7D32]" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold">Hoş geldiniz, {user?.name}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  EduFin'e hızlı bir kurulum ile başlayalım. Sadece 3 adım.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Para biriminiz</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="onboarding-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Sonra Ayarlar sayfasından değiştirebilirsiniz.</p>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={dismiss} data-testid="onboarding-skip">Şimdi değil</Button>
              <Button onClick={saveCurrency} disabled={saving} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="onboarding-next-1">
                Devam <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 mt-3" data-testid="onboarding-step-2">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FFF3E0] flex items-center justify-center flex-shrink-0">
                <Users size={20} weight="duotone" className="text-[#D97757]" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold">İlk öğrencinizi ekleyin</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Öğrenci ve veli bilgileri, saatlik ücret, paket ders — hepsi tek bir yerde.
                </p>
              </div>
            </div>
            <ul className="space-y-2 text-sm bg-muted/40 rounded-md p-4">
              <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-[#3A5A40] mt-0.5" /> Veli e-postası eklersek otomatik raporları oraya yollarız.</li>
              <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-[#3A5A40] mt-0.5" /> Saatlik ücret girin; ders süresinden otomatik hesaplanır.</li>
              <li className="flex items-start gap-2"><CheckCircle size={16} weight="duotone" className="text-[#3A5A40] mt-0.5" /> Paket ders (örn. 10 ders) takip edilir.</li>
            </ul>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(1)}>Geri</Button>
              <Button
                onClick={() => { dismiss(); nav("/ogrenciler"); }}
                className="bg-[#3A5A40] hover:bg-[#2E4833] text-white"
                data-testid="onboarding-go-students"
              >
                Öğrenci ekle <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
            <button
              onClick={() => setStep(3)}
              className="text-xs text-muted-foreground underline block mx-auto"
              data-testid="onboarding-next-2"
            >
              Sonra eklerim, ipuçlarına geçelim
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5 mt-3" data-testid="onboarding-step-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#EDF2F7] flex items-center justify-center flex-shrink-0">
                <CalendarBlank size={20} weight="duotone" className="text-[#2A6F97]" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold">Haftalık programı unutmayın</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Öğrenci eklerken haftalık slot (örn. Pzt 18:00–19:00) girerseniz, <b>tüm yıl boyunca</b> dersler otomatik oluşur.
                </p>
              </div>
            </div>
            <div className="bg-[#E8F5E9] border border-[#C8E6C9] rounded-md p-4 text-sm text-[#1A2E1C]">
              <b>İpucu:</b> <kbd className="font-mono bg-white px-1.5 py-0.5 rounded border border-border text-xs">?</kbd> tuşuyla klavye kısayollarına ulaşabilirsiniz.
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={dismiss} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="onboarding-finish">
                Başlayalım
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
